import { useRef, useState } from 'react';
import { SxProps } from '@mui/material';
import clsx from 'clsx';
import * as R from 'ramda';

import CreateAssetModal from '@atom/components/common/createAssetModal/CreateAssetModal';
import CreateWorkModal from '@atom/components/common/createWorkModal/CreateWorkModal';
import createAssetIcon from '@atom/components/common/svgIcons/createAsset.svg';
import createWorkIcon from '@atom/components/common/svgIcons/createWork.svg';
import createIcon from '@atom/components/common/svgIcons/globalCreate.svg';
import { usePreferences } from '@atom/hooks/usePreferences';
import { IconButton, Popover, Tooltip } from '@atom/mui';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  isInspectorAssetCreateRoleEnabled,
  isOrgAdminRequiredForInventoryCreate,
} from '@atom/utilities/tenantFeatureUtilities';

// TODO: Remove UDOT specific code and move to RBAC
import classes from './navigation.module.css';

const styles = {
  iconStyle: {
    height: '30px',
    marginRight: '1rem',
  },
};

const sx: SxProps = {
  width: '224px',
};

const CreateControls = () => {
  const anchor = useRef<HTMLDivElement>(null);
  const preferences = usePreferences();
  const requireWorkType = R.pathOr(
    false,
    ['workOrders', 'requireWorkType'],
    preferences,
  );

  const [open, setOpen] = useState<boolean>(false);
  const [assetModalOpen, setAssetModalOpen] = useState<boolean>(false);
  const [workModalOpen, setWorkModalOpen] = useState<boolean>(false);

  const canCreateAssets = isInspectorAssetCreateRoleEnabled
    ? hasRolePermissions(ROLE_SETS.INSPECTOR)
    : isOrgAdminRequiredForInventoryCreate
      ? hasRolePermissions(ROLE_SETS.ORG_ADMIN)
      : hasRolePermissions(ROLE_SETS.MANAGER);

  const tooltip = canCreateAssets ? 'Create' : 'Create Work';

  const handleClick = () => {
    return canCreateAssets ? setOpen(true) : setWorkModalOpen(true);
  };

  const handleAssetClick = () => {
    setOpen(false);
    setAssetModalOpen(true);
  };

  const handleWorkClick = () => {
    setOpen(false);
    setWorkModalOpen(true);
  };

  return (
    hasRolePermissions(ROLE_SETS.INSPECTOR) && (
      <>
        <div className={classes.additionalButtonsWrapper} ref={anchor}>
          <div
            className={clsx(classes.additionalButtonsContainer, classes.create)}
          >
            <IconButton onClick={handleClick} data-cy="globalCreate">
              <Tooltip placement="bottom-end" title={tooltip}>
                <img src={createIcon} />
              </Tooltip>
            </IconButton>
          </div>
        </div>
        <Popover
          open={open}
          anchorEl={anchor.current}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          onClose={() => setOpen(false)}
          slotProps={{ paper: { sx } }}
        >
          <div className={classes.menuRow} onClick={handleAssetClick}>
            <img style={styles.iconStyle} src={createAssetIcon} />
            <div>Create Inventory</div>
          </div>
          <div
            className={classes.menuRow}
            onClick={handleWorkClick}
            data-cy="globalCreateWork"
          >
            <img style={styles.iconStyle} src={createWorkIcon} />
            <div>Create Work</div>
          </div>
        </Popover>
        {canCreateAssets && (
          <CreateAssetModal
            open={assetModalOpen}
            onClose={() => setAssetModalOpen(false)}
          />
        )}
        <CreateWorkModal
          redirectOnCreate
          open={workModalOpen}
          workTemplateRequired={requireWorkType}
          onClose={() => setWorkModalOpen(false)}
        />
      </>
    )
  );
};

export default CreateControls;
